import {
  countryName, footerLinesOf, invoiceFileName, isCurrency, moneyLocaleFor, normalizeCountry, renderInvoicePdf, renderTaxNoticeForRates, swissQrBillFor, taxIdLabel,
  taxModeFromDb, taxWordFor, vatBreakdown, type Currency, type InvoicePdfModel, type PdfImage, type SwissQrBill,
} from "@talentkontor/domain";
import type { Api } from "../../data/api";
import { invoiceModelOf, invoicePdfOf, talentPartyOf } from "../../model/invoice-document";
import type { CInvoice, CInvoiceLine, CPayout, OrgAddress, TalentSelf } from "./data";
import type { DraftRecipient } from "./model";

/**
 * Rechnung des Creators als PDF aus seinen gespeicherten Angaben (Befund 6), mit demselben Schreiber wie die
 * Agentur-Rechnungen (packages/domain/src/invoice-pdf.ts). Grundlage sind die gespeicherte Rechnung und ihre Positionen:
 * was im PDF steht, ist genau das, was die Agentur prüft. Aussteller ist der Creator mit Anschrift, Steuernummer bzw.
 * USt-IdNr. und Bankverbindung; Empfänger die Agentur oder bei direkter Abrechnung die Marke.
 */

/** Höchstlänge des Logos als data-URL (wie die Datenbank, 0080) */
export const LOGO_MAX_CHARS = 400_000;

/**
 * Eigenes Logo des Creators (PARITAET-11) als Bild für das PDF: JPEG aus der data-URL, Breite und Höhe aus dem
 * SOF-Abschnitt. Kein gültiges JPEG: kein Logo (die Rechnung entsteht trotzdem).
 */
export function logoImageOf(dataUrl: string | null | undefined): PdfImage | null {
  const m = /^data:image\/jpeg;base64,([A-Za-z0-9+/=]+)$/.exec(dataUrl ?? "");
  if (!m) return null;
  let bytes: Uint8Array;
  try {
    const bin = atob(m[1]!);
    bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  } catch {
    return null;
  }
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let i = 2;
  while (i + 9 < bytes.length) {
    if (bytes[i] !== 0xff) return null;
    const marker = bytes[i + 1]!;
    const len = (bytes[i + 2]! << 8) | bytes[i + 3]!;
    // SOF0 … SOF15 ohne DHT (C4), JPG (C8) und DAC (CC)
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      const height = (bytes[i + 5]! << 8) | bytes[i + 6]!;
      const width = (bytes[i + 7]! << 8) | bytes[i + 8]!;
      return width > 0 && height > 0 ? { jpeg: bytes, width, height } : null;
    }
    i += 2 + len;
  }
  return null;
}

export function creatorInvoicePdfModel(input: {
  invoice: CInvoice;
  lines: readonly Pick<CInvoiceLine, "description" | "quantity" | "unit_cents" | "net_cents" | "tax_rate_bp">[];
  talent: TalentSelf;
  payout: CPayout | null;
  recipient: DraftRecipient;
}): InvoicePdfModel {
  const { invoice: i, talent: t, payout, recipient } = input;
  const lang = recipient.language;
  const issuerCountry = normalizeCountry(t.country);
  // Aussteller ist die Person mit vollem Namen bzw. ihre Firma, nie der Anzeigename im Portal (Befund Creator 5)
  const party = talentPartyOf(t as unknown as Record<string, unknown>) ?? { name: [t.first_name, t.last_name].filter(Boolean).join(" ") };
  const name = party.name ?? [t.first_name, t.last_name].filter(Boolean).join(" ");
  const country = (c: string | null) => {
    const code = normalizeCountry(c);
    return code ? countryName(code, lang) : null;
  };
  const mode = taxModeFromDb(i.tax_mode);
  const issuerFields = [
    ...(t.tax_number?.trim() ? [{ label: lang === "en" ? "Tax number" : "Steuernummer", value: t.tax_number.trim() }] : []),
    // Kleinunternehmer im Inland: USt-IdNr. nur ohne Steuernummer (ST-10); eine der beiden ist Pflicht (§ 34a Satz 1 Nr. 2 UStDV)
    ...(t.vat_id?.trim() && (mode !== "SMALL_BUSINESS" || !t.tax_number?.trim()) ?[{ label: taxIdLabel(issuerCountry, lang), value: t.vat_id.trim() }] : []),
  ];
  const rates = [...new Set(input.lines.map((l) => l.tax_rate_bp ?? 0))];
  const currency = (isCurrency(i.currency) ? i.currency : "EUR") as Currency;
  const recipientCountry = normalizeCountry(recipient.country);
  const payee = payout && (payout.iban || payout.account_number || payout.payout_email) ? payout : null;
  return {
    language: lang,
    locale: moneyLocaleFor(lang, issuerCountry),
    currency,
    number: i.number ?? (lang === "en" ? "DRAFT" : "ENTWURF"),
    invoiceDate: i.issue_date,
    serviceMonth: null,
    service: i.service_period_start ? { from: i.service_period_start, to: i.service_period_end ?? i.service_period_start } : null,
    dueDate: i.issuer_due_date ?? i.document_due_date,
    issuer: {
      name,
      addressLines: [t.street, [t.postal_code, t.city].filter(Boolean).join(" "), country(t.country)].filter((x): x is string => !!x && !!x.trim()),
      fields: issuerFields,
    },
    recipient: {
      name: recipient.name,
      addressLines: recipient.lines.map((l) => (recipientCountry && normalizeCountry(l) === recipientCountry ? country(l) ?? l : l)),
      fields: recipient.vatId ? [{ label: taxIdLabel(recipientCountry, lang), value: recipient.vatId }] : [],
    },
    lines: input.lines.map((l) => ({ description: l.description, quantity: Number(l.quantity ?? 1), unitPriceCents: l.unit_cents, totalCents: l.net_cents })),
    totals: {
      netCents: i.net_cents, vatCents: i.tax_cents, grossCents: i.gross_cents,
      ratePercent: rates.length === 1 ? (rates[0] ?? 0) / 100 : null,
      // L2: Positionen mit verschiedenen Sätzen: je Satz Entgelt und Steuer (§ 14 Abs. 4 Satz 1 Nr. 7 und 8 UStG)
      byRate: vatBreakdown(input.lines.map((l) => ({ netCents: l.net_cents, rateBp: l.tax_rate_bp })), i.tax_cents),
      // K-280: Creator in CH/LI ohne MWST-Registrierung: keine Zeile „MWST 0.00“; ebenso Reverse Charge und Leistung ins Ausland
      hideVat: (issuerCountry === "CH" || issuerCountry === "LI") && Number(i.tax_cents) === 0
        && (mode === "SMALL_BUSINESS" || mode === "REVERSE_CHARGE_EU" || mode === "REVERSE_CHARGE_DOMESTIC" || mode === "NOT_TAXABLE_ABROAD"),
    },
    // Abnahme E-7: bei mehreren Sätzen „Enthält Umsatzsteuer (19 % und 7 %).“ (vorher stand dort 0 %)
    taxNotice: i.notice_key ? renderTaxNoticeForRates(i.notice_key, lang, rates.map((bp) => (bp ?? 0) / 100)) : null,
    // Zahlungsweg des Creators: Konto (SEPA, SWIFT mit Kontonummer und Routing) oder PayPal/Wise mit Adresse (Befund Creator 15)
    bank: payout && (payout.iban || payout.account_number || payout.payout_email) ? {
      method: payout.payout_method ?? "sepa", accountHolder: payout.bank_account_holder ?? [t.first_name, t.last_name].filter(Boolean).join(" "),
      iban: payout.iban, bic: payout.bic, bankName: payout.bank_name, bankCountry: payout.bank_country,
      accountNumber: payout.account_number ?? null, routingCode: payout.routing_code ?? null, email: payout.payout_email ?? null,
    } : null,
    reference: null,
    footerLines: footerLinesOf({ ...party, email: null }, lang),
    // Steuerzeile „MWST“ bei Creatorn aus der Schweiz und Liechtenstein (wie bei den Belegen der Agentur)
    taxLabel: taxWordFor(issuerCountry, lang),
    // Eigenes Logo des Creators oben auf der ersten Seite (PARITAET-11, wie im Altportal TA-087)
    logo: logoImageOf(t.invoice_logo),
    qrBill: creatorQrBill({ number: i.number, currency, grossCents: i.gross_cents, lang, party, payout: payee, recipient }),
    // 0267 (K-296): eingefrorene Umsatzsteuer in Euro bei fremder Rechnungswährung
    taxEur: i.tax_eur_cents != null && i.tax_eur_rate != null && i.tax_eur_rate_on
      ? { cents: Number(i.tax_eur_cents), rate: Number(i.tax_eur_rate), on: String(i.tax_eur_rate_on).slice(0, 10), source: i.tax_eur_source ?? null } : null,
  };
}

/**
 * Bekommt die Rechnung den QR-Zahlteil? Aus demselben Modell wie das PDF (Aussteller über talentPartyOf, nicht der rohe
 * TalentSelf ohne Feld „name“), damit der Hinweis im Assistenten und das PDF nie auseinanderlaufen (Befund L1).
 */
export function creatorHasQrBill(input: Parameters<typeof creatorInvoicePdfModel>[0]): boolean {
  return creatorInvoicePdfModel(input).qrBill != null;
}

/**
 * Schweizer QR-Zahlteil auf der Rechnung des Creators (0069): gleiche Fachlogik wie bei der Agentur. Zahlungsempfänger ist der
 * Creator (Aussteller), Zahlungspflichtiger die Agentur bzw. die Marke; nur mit Konto in der Schweiz oder Liechtenstein, in CHF
 * oder EUR, mit Rechnungsnummer. Referenz: Creditor Reference aus der eigenen Rechnungsnummer (Creator haben keine QR-IBAN).
 */
export function creatorQrBill(x: {
  number: string | null; currency: Currency; grossCents: number; lang: "de" | "en";
  party: { name?: string | null; street?: string | null; postal_code?: string | null; city?: string | null; country?: string | null };
  payout: Pick<CPayout, "iban" | "payout_method"> | null; recipient: Pick<DraftRecipient, "address">;
}): SwissQrBill | null {
  if (!x.payout?.iban || x.payout.payout_method === "paypal" || x.payout.payout_method === "wise") return null;
  return swissQrBillFor({
    number: x.number, language: x.lang, currency: x.currency, grossCents: x.grossCents, creditor: x.party, iban: x.payout.iban,
    debtor: x.recipient.address ?? null,
  }).bill;
}

/**
 * Beleg der Agentur an den Creator bzw. Gutschrift als PDF (Befund 5): das archivierte PDF, sonst aus den beim Festschreiben
 * eingefrorenen Angaben neu erzeugt (dieselbe Funktion wie bei der Agentur).
 */
export async function agencyDocumentPdf(api: Api, i: CInvoice, org: OrgAddress, talent: TalentSelf): Promise<{ blob: Blob; fileName: string }> {
  if (i.pdf_path) {
    const blob = await api.download("documents", i.pdf_path).catch(() => null);
    if (blob && (blob.type === "application/pdf" || (await blob.slice(0, 5).text()) === "%PDF-")) {
      return { blob, fileName: i.pdf_path.split("/").pop()?.replace(/^\d+_/, "") ?? `${i.number ?? "Rechnung"}.pdf` };
    }
  }
  const lines = await api.select<CInvoiceLine>("invoice_lines", { filters: [["invoice_id", "eq", i.id]], order: [["position", "asc"]] });
  const o = org as unknown as Record<string, unknown>;
  const model = invoiceModelOf({
    invoice: i as unknown as Record<string, unknown>,
    lines: lines.map((l) => ({ description: l.description, quantity: l.quantity, unit_cents: l.unit_cents, net_cents: l.net_cents, tax_rate_bp: l.tax_rate_bp ?? null })),
    org: { name: (o.legal_name as string | null) ?? org.name, street: o.street as string | null, postal_code: o.postal_code as string | null, city: o.city as string | null, country: o.country as string | null, vat_id: o.vat_id as string | null },
    talent: talentPartyOf(talent as unknown as Record<string, unknown>), brand: null, bankFallback: null, noticeFallback: null, collectiveMonths: [], original: null,
    language: (i as unknown as { document_language?: string | null }).document_language === "en" ? "en" : "de",
  });
  const f = invoicePdfOf(model);
  return { blob: new Blob([f.bytes as BlobPart], { type: "application/pdf" }), fileName: f.filename };
}

/** PDF-Bytes und Dateiname (Rechnung_<Nummer>_<Aussteller>_<Empfänger>.pdf). */
export function creatorInvoicePdf(input: Parameters<typeof creatorInvoicePdfModel>[0], opts: { now?: Date } = {}): { bytes: Uint8Array; fileName: string } {
  const model = creatorInvoicePdfModel(input);
  return {
    bytes: renderInvoicePdf(model, opts),
    fileName: invoiceFileName({ number: model.number, issuerName: model.issuer.name, recipientName: model.recipient.name, language: model.language }),
  };
}
